// src/components/dashboard/TablerosAdminFilters.jsx
// Filtros de año por criterio para la página /tableros (que era /tableros-v2).
// Fila 1: Select de criterio (Actual por año exp / Pago anticipo o total)
// Fila 2: Select Año (multi) | Select Gestión | Select Mes (placeholder)
import React from 'react';
import Select from 'react-select';
import { optionsPartido, optionsEstado, optionsAreaResponsable } from '../../datos/options';

// Años y gestiones ordenados del más actual (arriba) al más antiguo
const ALL_YEARS = ['2027','2026','2025','2024','2023','2022','2021','2020','2019','2018','2017','2016'];

const GESTIONES = [
	{ key: '2da', label: '2da Gestión (2024–2027)', years: ['2027','2026','2025','2024'] },
	{ key: '1era', label: '1era Gestión (2020–2023)', years: ['2023','2022','2021','2020'] },
];

const CRITERIOS = [
	{ value: 'expediente',   label: 'Actual (por año exp. de proyecto)' },
	{ value: 'pago_anticipo', label: 'Pago (anticipo o total)' },
];

const yearOptions = ALL_YEARS.map(y => ({ value: y, label: y }));

const gestionOptions = GESTIONES.map(g => ({ value: g.key, label: g.label }));

const MESES = [
	{ value: '01', label: 'Enero' },
	{ value: '02', label: 'Febrero' },
	{ value: '03', label: 'Marzo' },
	{ value: '04', label: 'Abril' },
	{ value: '05', label: 'Mayo' },
	{ value: '06', label: 'Junio' },
	{ value: '07', label: 'Julio' },
	{ value: '08', label: 'Agosto' },
	{ value: '09', label: 'Septiembre' },
	{ value: '10', label: 'Octubre' },
	{ value: '11', label: 'Noviembre' },
	{ value: '12', label: 'Diciembre' },
];

const mesOptions = MESES.map(m => ({ value: m.value, label: m.label }));

const TablerosAdminFilters = ({
	filters,
	onFilterChange,
	onReset,
	loading = false,
	isRefetching = false
}) => {
	const criterioYear = filters.criterioYear || 'pago_anticipo';

	const selectedYears = Array.isArray(filters.year)
		? filters.year
		: (filters.year && filters.year !== 'Todos' && filters.year !== 'Todas')
			? [filters.year]
			: [];

	const selectedYearOptions = yearOptions.filter(o => selectedYears.includes(o.value));

	const handleCriterioChange = (criterio) => {
		onFilterChange({ target: { name: 'criterioYear', value: criterio } });
		onFilterChange({ target: { name: 'year', value: 'Todos' } });
		onFilterChange({ target: { name: 'mes', value: 'Todos' } });
	};

	const handleGestionChange = (selected) => {
		if (!selected || selected.length === 0) {
			onFilterChange({ target: { name: 'year', value: 'Todos' } });
			return;
		}
		const gestiones = GESTIONES.filter(g => selected.some(o => o.value === g.key));
		const years = [...new Set(gestiones.flatMap(g => g.years))].sort((a, b) => b.localeCompare(a));
		onFilterChange({ target: { name: 'year', value: years } });
	};

	const handleYearsChange = (selected) => {
		if (!selected || selected.length === 0) {
			onFilterChange({ target: { name: 'year', value: 'Todos' } });
		} else {
			onFilterChange({ target: { name: 'year', value: selected.map(o => o.value) } });
		}
	};

	// ---- Partido (múltiple) ----
	const partidosOptions = [
		{ value: 'Todos', label: '⚠️ Todos los Partidos' },
		...optionsPartido.map(p => ({
			value: p.value,
			label: `${p.label} (${p.seccion_electoral})`
		}))
	];

	const getPartidosValue = () => {
		const current = filters.partido || 'Todos';
		if (current === 'Todos' || current === 'Todas' || current === []) {
			return [{ value: 'Todos', label: '⚠️ Todos los Partidos' }];
		}
		const arr = Array.isArray(current) ? current : current.split(',');
		return optionsPartido
			.filter(p => arr.includes(String(p.value)))
			.map(p => ({ value: p.value, label: `${p.label} (${p.seccion_electoral})` }));
	};

	const handlePartidosChange = (selected) => {
		if (!selected || selected.length === 0) {
			onFilterChange({ target: { name: 'partido', value: 'Todos' } });
			return;
		}
		const valores = selected.map(o => o.value);
		let resultado = valores;
		const teniaTodos = (filters.partido === 'Todos' || filters.partido === 'Todas' || !filters.partido);
		if (valores.includes('Todos') && valores.length > 1) {
			resultado = teniaTodos ? valores.filter(v => v !== 'Todos') : ['Todos'];
		}
		onFilterChange({ target: { name: 'partido', value: resultado } });
	};

	// ---- Estado (múltiple) ----
	const estadosUnicos = [
		...optionsEstado.map(e => e.value),
		'Adenda', 'Desestimada', 'En Ejecución'
	].filter((v, i, arr) => arr.indexOf(v) === i);

	const estadosOptions = [
		{ value: 'Todos', label: '⚠️ Todos los Estados' },
		...estadosUnicos.map(e => ({ value: e, label: e }))
	];

	const getEstadosValue = () => {
		const current = filters.estado || 'Todos';
		if (current === 'Todos' || current === 'Todas' || (Array.isArray(current) && current.length === 0)) {
			return [{ value: 'Todos', label: '⚠️ Todos los Estados' }];
		}
		const arr = Array.isArray(current) ? current : current.split(',');
		return estadosUnicos
			.filter(e => arr.includes(e))
			.map(e => ({ value: e, label: e }));
	};

	const handleEstadosChange = (selected) => {
		if (!selected || selected.length === 0) {
			onFilterChange({ target: { name: 'estado', value: 'Todos' } });
			return;
		}
		const valores = selected.map(o => o.value);
		let resultado = valores;
		const teniaTodos = (filters.estado === 'Todos' || filters.estado === 'Todas' || !filters.estado);
		if (valores.includes('Todos') && valores.length > 1) {
			resultado = teniaTodos ? valores.filter(v => v !== 'Todos') : ['Todos'];
		}
		onFilterChange({ target: { name: 'estado', value: resultado } });
	};

	// Gestiones activas según los años seleccionados:
	// una gestión se marca como activa SOLO si está completa en la selección
	// (todos sus años seleccionados). Así, un año suelto (ej. 2026) NO marca
	// la gestión 2024-2027, pero si se seleccionan todos sus años se activa.
	const gestionesActivas = GESTIONES
		.filter(g => selectedYears.length > 0 && g.years.every(y => selectedYears.includes(y)))
		.map(g => g.key);

	const gestionValue = gestionesActivas.length > 0
		? gestionOptions.filter(o => gestionesActivas.includes(o.value))
		: [];

	// ---- Mes (múltiple) ----
	// Solo aplica al criterio pago_anticipo (mes del anticipo). En otros criterios
	// el select queda deshabilitado.
	const mesHabilitado = criterioYear === 'pago_anticipo';

	const mesOptionsConTodos = [
		{ value: 'Todos', label: '⚠️ Todos los Meses' },
		...mesOptions
	];

	const getMesesValue = () => {
		const current = filters.mes || 'Todos';
		if (current === 'Todos' || current === 'Todas' || (Array.isArray(current) && current.length === 0)) {
			return [{ value: 'Todos', label: '⚠️ Todos los Meses' }];
		}
		const arr = Array.isArray(current) ? current : current.split(',');
		return mesOptions.filter(m => arr.includes(m.value));
	};

	const handleMesesChange = (selected) => {
		if (!selected || selected.length === 0) {
			onFilterChange({ target: { name: 'mes', value: 'Todos' } });
			return;
		}
		const valores = selected.map(o => o.value);
		let resultado = valores;
		const teniaTodos = (filters.mes === 'Todos' || filters.mes === 'Todas' || !filters.mes);
		if (valores.includes('Todos') && valores.length > 1) {
			resultado = teniaTodos ? valores.filter(v => v !== 'Todos') : ['Todos'];
		}
		onFilterChange({ target: { name: 'mes', value: resultado } });
	};

	return (
		<div className="mb-6 rounded-xl border border-gray-200 bg-white shadow-sm transition-all">
			{/* Header */}
			<div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
				<div className="flex items-center gap-2">
					<svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
					</svg>
					<h3 className="font-semibold text-gray-700">Filtros de Año</h3>
					{(criterioYear !== 'pago_anticipo' || selectedYears.length > 0) && (
						<span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
							{[criterioYear !== 'pago_anticipo' ? 1 : 0, selectedYears.length > 0 ? 1 : 0].reduce((a,b) => a+b, 0)}
						</span>
					)}
				</div>
				<button
					onClick={onReset}
					disabled={loading}
					className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
				>
					<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
					</svg>
					Resetear
				</button>
			</div>

			<div className="p-4">
				{/* Fila 1: Criterio | Partido | Estado */}
				<div className="flex flex-wrap items-end gap-x-4">
					<div className="min-w-[170px] flex-none w-[220px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
							Criterio de año
						</label>
						<select
							value={criterioYear}
							onChange={(e) => handleCriterioChange(e.target.value)}
							disabled={loading}
							className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{CRITERIOS.map((c) => (
								<option key={c.value} value={c.value}>
									{c.label}
								</option>
							))}
						</select>
					</div>

					{/* Selector de Partido (múltiple) */}
					<div className="flex-1 min-w-[250px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
							Partidos / Municipios
						</label>
						<Select
							isMulti
							options={partidosOptions}
							value={getPartidosValue()}
							onChange={handlePartidosChange}
							isDisabled={loading}
							isClearable
							placeholder="Seleccionar partidos..."
							closeMenuOnSelect={false}
							className="text-sm"
							styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
						/>
					</div>

					{/* Selector de Estado (múltiple) */}
					<div className="flex-1 min-w-[200px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
							Estado
						</label>
						<Select
							isMulti
							options={estadosOptions}
							value={getEstadosValue()}
							onChange={handleEstadosChange}
							isDisabled={loading}
							isClearable
							placeholder="Seleccionar estados..."
							closeMenuOnSelect={false}
							className="text-sm"
							styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
						/>
					</div>

					{/* Selector de Área Responsable */}
					<div className="flex-1 min-w-[200px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">
							Área Responsable
						</label>
						<select
							value={filters.area_responsable || 'Todos'}
							onChange={(e) => onFilterChange({ target: { name: 'area_responsable', value: e.target.value } })}
							disabled={loading}
							className="w-full rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<option value="Todos">Todas las áreas</option>
							{optionsAreaResponsable.map((a) => (
								<option key={a.value} value={a.value}>{a.label}</option>
							))}
						</select>
					</div>
				</div>

				{/* Fila 2: Año | Gestión | Mes */}
				<div className="mt-4 flex flex-wrap items-end gap-x-4">
					{/* Select Año (multi) */}
					<div className="flex-1 min-w-[180px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Año</label>
						<Select
							isMulti
							options={yearOptions}
							value={selectedYearOptions}
							onChange={handleYearsChange}
							isDisabled={loading}
							isClearable={selectedYears.length > 0}
							placeholder="Todos los años..."
							closeMenuOnSelect={false}
							className="text-sm"
							styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
						/>
					</div>

					{/* Select Gestión */}
					<div className="flex-1 min-w-[220px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Gestión</label>
						<Select
							isMulti
							options={gestionOptions}
							value={gestionValue}
							onChange={handleGestionChange}
							isDisabled={loading}
							isClearable
							placeholder="Seleccionar gestión..."
							closeMenuOnSelect={false}
							className="text-sm"
							styles={{ control: (base) => ({ ...base, minHeight: '38px' }) }}
						/>
					</div>

					{/* Select Mes (múltiple) — solo con criterio pago_anticipo */}
					<div className="min-w-[180px]">
						<label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-500">Mes</label>
						<Select
							isMulti
							options={mesOptionsConTodos}
							value={getMesesValue()}
							onChange={handleMesesChange}
							isDisabled={loading || !mesHabilitado}
							isClearable
							placeholder={mesHabilitado ? "Seleccionar mes..." : "Solo pago anticipo"}
							closeMenuOnSelect={false}
							className="text-sm"
							styles={{
								control: (base) => ({
									...base,
									minHeight: '38px',
									...(mesHabilitado ? {} : { backgroundColor: '#f3f4f6', cursor: 'not-allowed' })
								}),
								placeholder: (base) => ({ ...base, color: '#9ca3af' })
							}}
						/>
						{!mesHabilitado && (
							<p className="mt-1 text-[10px] text-gray-400">Disponible solo con "Pago (anticipo o total)"</p>
						)}
					</div>
				</div>
			</div>

			{/* Refetch indicator */}
			{(loading || isRefetching) && (
				<div className="border-t border-gray-100 px-4 py-2">
					<span className="flex items-center gap-2 text-xs text-gray-400 animate-pulse">
						<svg className="h-4 w-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
						</svg>
						Sincronizando datos...
					</span>
				</div>
			)}
		</div>
	);
};

export default TablerosAdminFilters;
